import { getCurrencyFormatter } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import type { OutcomeAttributionConfig } from "@redotech/redo-attribution-common/attribution-settings";
import DownloadIcon from "@redotech/redo-icons/download-01.svg";
import {
  buildPerformanceTree,
  filterPerformanceRows,
  PERFORMANCE_AUDIENCE_LABELS,
  PERFORMANCE_AUTHOR_LABELS,
  PERFORMANCE_CHANNEL_LABELS,
  PERFORMANCE_DIMENSION_LABELS,
  PERFORMANCE_DIMENSIONS,
  PERFORMANCE_TYPE_LABELS,
  PerformanceDimension,
  visibleNodes,
  type PerformanceFilters,
  type PerformanceNode,
  type PerformanceRow,
} from "@redotech/redo-marketing-common/performance-report";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { ATTRIBUTION_QUERY_OPTIONS } from "@redotech/redo-merchant-app-common/marketing/attribution-query-options";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { ChartCard } from "@redotech/redo-web/arbiter-components/charts/chart-card/chart-card";
import { RedoDateRangePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-date-range-picker";
import {
  MetricItemContainer,
  MetricItemContent,
  MetricItemHeading,
} from "@redotech/redo-web/arbiter-components/metric-item/redo-metric-item";
import { RedoProgressBar } from "@redotech/redo-web/arbiter-components/progress-bar/redo-progress-bar";
import {
  RedoTableEmptyStateContent,
  RedoTableLoadingStateContent,
  type RedoTableColumn,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { RedoTableCard } from "@redotech/redo-web/arbiter-components/tables/redo-table-card";
import { RedoTableTextCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { Flex } from "@redotech/redo-web/flex";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { SkeletonText } from "@redotech/redo-web/skeleton";
import { ColumnAlignment } from "@redotech/redo-web/table";
import { Text } from "@redotech/redo-web/text";
import { deepIntersectionEquals } from "@redotech/util/equal";
import { downloadBlob } from "@redotech/web-util/download";
import { keepPreviousData } from "@tanstack/react-query";
import { stringify } from "csv-stringify/browser/esm/sync";
import { memo, useContext, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { AttributionControls } from "./performance/attribution-controls";
import { AttributionFilters } from "./performance/attribution-filters";
import {
  BarCell,
  NumberCell,
  TreeLabelCell,
} from "./performance/performance-cells";

const STALE_TIME_MS = 5 * 60 * 1000;

/** Long enough that typing a window runs one report, not one per digit. */
const CONTROLS_DEBOUNCE_MS = 400;

/** Today is the store's, since the range's edges are the store's midnights. */
function last30Days(
  timeZone: string,
): [Temporal.PlainDate, Temporal.PlainDate] {
  const today = Temporal.Now.plainDateISO(timeZone);
  return [today.subtract({ days: 29 }), today];
}

export const MarketingAnalyticsPerformance = memo(
  function MarketingAnalyticsPerformance() {
    const team = useRequiredContext(TeamContext);
    const { currency } = useContext(CurrencyContext);
    // Whole units on screen; the CSV export keeps the cents.
    const formatCurrency = useMemo(() => {
      const formatter = getCurrencyFormatter(currency, undefined, undefined, {
        maximumFractionDigits: 0,
      });
      return (amount: number) => formatter.format(amount);
    }, [currency]);
    const timeZone = team.ianaTimezone ?? Temporal.Now.timeZoneId();
    const [dateRange, setDateRange] = useState(() => last30Days(timeZone));
    const [controls, setControls] = useState<OutcomeAttributionConfig | null>(
      null,
    );
    const reportControls = useDebounce(controls, CONTROLS_DEBOUNCE_MS);

    const startOfDay = (date: Temporal.PlainDate) =>
      date.toZonedDateTime({ timeZone }).toInstant();
    const range = {
      from: startOfDay(dateRange[0]),
      to: startOfDay(dateRange[1].add({ days: 1 })),
    };
    const { data, isPending, isPlaceholderData } = useRpcQuery(
      RedoMarketingRpcClientContext,
      "getPerformanceReport",
      { ...range, controls: reportControls },
      {
        ...ATTRIBUTION_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        staleTime: STALE_TIME_MS,
      },
    );
    const loading = isPending || isPlaceholderData;
    const controlsChanged =
      controls !== null &&
      data !== undefined &&
      !deepIntersectionEquals(controls, data.saved);

    const attributed = (data?.rows ?? []).reduce(
      (sum, row) => sum + row.revenue,
      0,
    );
    const storeShare =
      data && data.store.revenue > 0
        ? (attributed / data.store.revenue) * 100
        : 0;
    const defaultAttributed = data?.defaultRows?.reduce(
      (sum, row) => sum + row.revenue,
      0,
    );

    const exportCsv = useHandler(async (rows: readonly PerformanceRow[]) => {
      if (data) {
        await downloadBlob(
          new Blob([stringify(csvRows(rows))], { type: "text/csv" }),
          `attribution-${dateRange[0].toString()}-${dateRange[1].toString()}.csv`,
        );
      }
    });

    return (
      <Flex dir="column" w="full">
        <Flex dir="column" gap="4xl" maxW="3xl" mx="auto" pb="8xl" w="full">
          <Flex align="center" dir="row" gap="lg">
            <RedoDateRangePicker
              dateRange={dateRange}
              maxDate={Temporal.Now.plainDateISO(timeZone)}
              setDateRange={(picked) => {
                if (picked) {
                  setDateRange(picked);
                }
              }}
              size="xs"
            />
          </Flex>
          <Flex align="flex-start" dir="row" gap="xl" w="full">
            <Flex dir="column" flex="1" gap="xl" minW="none">
              <MetricItemContainer>
                <MetricItemContent>
                  <Flex gap="xl">
                    <SummaryStat
                      description={
                        data
                          ? `All orders, ${data.store.orders.toLocaleString()} total`
                          : undefined
                      }
                      pending={loading}
                      title="Store revenue"
                      value={data ? formatCurrency(data.store.revenue) : ""}
                    />
                    <SummaryStat
                      description={[
                        "Performance attribution",
                        `${storeShare.toFixed(1)}% of store revenue`,
                        ...(defaultAttributed === undefined
                          ? []
                          : [
                              `${signedAmount(attributed - defaultAttributed, formatCurrency)} vs. default`,
                            ]),
                      ].join(" · ")}
                      pending={loading}
                      title="Attributed to Redo"
                      value={data ? formatCurrency(attributed) : ""}
                    />
                  </Flex>
                  <RedoProgressBar
                    labelStyle="none"
                    percentage={Math.round(storeShare)}
                    style="thin"
                  />
                </MetricItemContent>
              </MetricItemContainer>
              <AttributionTable
                defaultRows={data?.defaultRows ?? null}
                exportCsv={exportCsv}
                formatCurrency={formatCurrency}
                loading={loading}
                rows={data?.rows ?? []}
              />
            </Flex>
            <Flex position="sticky" shrink={0} top="xl" w="xxs">
              <ChartCard
                actions={
                  // Hidden, not removed, so the header keeps its height.
                  <Flex visibility={controlsChanged ? "visible" : "hidden"}>
                    <RedoButton
                      hierarchy="tertiary"
                      onClick={() => setControls(null)}
                      size="xs"
                      text="Reset"
                    />
                  </Flex>
                }
                minWidth="0px"
                subtitle="Changes apply to this view only and aren't saved."
                title="Attribution controls"
                variant="single"
              >
                <AttributionControls
                  config={controls ?? data?.saved}
                  setConfig={setControls}
                />
              </ChartCard>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    );
  },
);

function signedAmount(
  delta: number,
  formatCurrency: (amount: number) => string,
): string {
  return `${delta < 0 ? "-" : "+"}${formatCurrency(Math.abs(delta))}`;
}

const SummaryStat = memo(function SummaryStat({
  title,
  value,
  description,
  pending,
}: {
  title: string;
  value: string;
  description: string | undefined;
  pending: boolean;
}) {
  return (
    <Flex dir="column" flex="1" gap="lg">
      <MetricItemHeading title={title} />
      {pending ? (
        <SkeletonText height="30px" length={16} />
      ) : (
        <Text fontSize="xl" fontWeight="bold" textColor="primary">
          {value}
        </Text>
      )}
      {description && (
        <Text fontSize="xs" textColor="secondary">
          {description}
        </Text>
      )}
    </Flex>
  );
});

function csvRows(rows: readonly PerformanceRow[]): string[][] {
  const total = rows.reduce((sum, row) => sum + row.revenue, 0);
  return [
    ["Type", "Channel", "Author", "Audience", "Revenue", "Orders", "Share"],
    ...rows.map((row) => [
      PERFORMANCE_TYPE_LABELS[row.type],
      PERFORMANCE_CHANNEL_LABELS[row.channel],
      PERFORMANCE_AUTHOR_LABELS[row.author],
      PERFORMANCE_AUDIENCE_LABELS[row.audience],
      row.revenue.toFixed(2),
      row.orders.toFixed(2),
      total > 0 ? `${((row.revenue / total) * 100).toFixed(1)}%` : "",
    ]),
  ];
}

const AttributionTable = memo(function AttributionTable({
  rows,
  defaultRows,
  formatCurrency,
  loading,
  exportCsv,
}: {
  rows: readonly PerformanceRow[];
  defaultRows: readonly PerformanceRow[] | null;
  formatCurrency(amount: number): string;
  loading: boolean;
  exportCsv(rows: readonly PerformanceRow[]): void;
}) {
  const hasRows = rows.length > 0;
  const refreshing = loading && hasRows;
  const [groupBy, setGroupBy] = useState<PerformanceDimension[]>(
    PERFORMANCE_DIMENSIONS,
  );
  const [filters, setFilters] = useState<PerformanceFilters>({});
  const filtered = useMemo(
    () => filterPerformanceRows(rows, filters),
    [rows, filters],
  );
  const filtering = filtered.length !== rows.length;
  const tree = useMemo(
    () =>
      buildPerformanceTree(
        filtered,
        defaultRows && filterPerformanceRows(defaultRows, filters),
        groupBy,
      ),
    [filtered, defaultRows, filters, groupBy],
  );
  const total = tree.reduce((sum, node) => sum + node.revenue, 0);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const toggle = useHandler((node: PerformanceNode) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (!next.delete(node.id)) {
        next.add(node.id);
      }
      return next;
    }),
  );
  const share = (revenue: number) => (total > 0 ? (revenue / total) * 100 : 0);

  const columns: RedoTableColumn<PerformanceNode>[] = [
    {
      key: "type",
      widthMode: { type: "fill", relativeLength: 3 },
      renderHeaderCell: () =>
        StandardRedoTableHeaderCell({
          headerText:
            PERFORMANCE_DIMENSION_LABELS[
              groupBy[0] ?? PerformanceDimension.TYPE
            ],
        }),
      renderNormalCell: ({ row }) => (
        <TreeLabelCell
          collapsed={collapsed.has(row.id)}
          depth={row.depth}
          expandable={row.children.length > 0}
          label={row.label}
        />
      ),
      renderSummaryCell: () => (
        <RedoTableTextCell fontWeight="semibold" text="Total" />
      ),
    },
    {
      key: "bar",
      widthMode: { type: "fill", relativeLength: 3 },
      renderHeaderCell: () => StandardRedoTableHeaderCell({ headerText: "" }),
      renderNormalCell: ({ row }) => (
        <BarCell loading={refreshing} percentage={share(row.revenue)} />
      ),
      renderSummaryCell: () => (
        <RedoTableTextCell
          text={filtering ? "Filtered rows only" : "Matches attributed revenue"}
          textColor="secondary"
        />
      ),
    },
    {
      key: "revenue",
      widthMode: { type: "hug" },
      alignment: "right",
      renderHeaderCell: () =>
        StandardRedoTableHeaderCell({
          headerText: "Revenue",
          alignment: ColumnAlignment.RIGHT,
        }),
      renderNormalCell: ({ row }) => (
        <NumberCell loading={refreshing} text={formatCurrency(row.revenue)} />
      ),
      renderSummaryCell: () => (
        <NumberCell
          fontWeight="semibold"
          loading={refreshing}
          text={formatCurrency(total)}
        />
      ),
    },
    {
      key: "share",
      widthMode: { type: "hug" },
      alignment: "right",
      renderHeaderCell: () =>
        StandardRedoTableHeaderCell({
          headerText: "Share",
          alignment: ColumnAlignment.RIGHT,
        }),
      renderNormalCell: ({ row }) => (
        <NumberCell
          loading={refreshing}
          text={`${share(row.revenue).toFixed(1)}%`}
          textColor="secondary"
        />
      ),
      renderSummaryCell: () => null,
    },
  ];
  if (defaultRows) {
    columns.push({
      key: "vsDefault",
      widthMode: { type: "hug" },
      alignment: "right",
      renderHeaderCell: () =>
        StandardRedoTableHeaderCell({
          headerText: "vs. default",
          alignment: ColumnAlignment.RIGHT,
        }),
      renderNormalCell: ({ row }) => {
        const delta = row.revenue - (row.defaultRevenue ?? 0);
        return (
          <NumberCell
            loading={refreshing}
            text={signedAmount(delta, formatCurrency)}
            textColor="secondary"
          />
        );
      },
      renderSummaryCell: () => null,
    });
  }

  return (
    <RedoTableCard<PerformanceNode>
      rightButtonProps={{
        disabled: loading || rows.length === 0,
        hierarchy: "secondary",
        IconLeading: DownloadIcon,
        onClick: () => exportCsv(filtered),
        size: "xs",
        text: "Export CSV",
      }}
      subtitle={
        <AttributionFilters
          filters={filters}
          groupBy={groupBy}
          setFilters={setFilters}
          setGroupBy={setGroupBy}
        />
      }
      tableProps={{
        emptyStateContent: (
          <RedoTableEmptyStateContent
            text={
              filtering
                ? "No attributed orders match these filters."
                : "No attributed orders in this date range."
            }
          />
        ),
        isRowClickable: (row) => row.children.length > 0,
        layout: { columns },
        loadingStateContent:
          loading && !hasRows ? <RedoTableLoadingStateContent /> : null,
        onRowClick: toggle,
        rowsOfData: loading && !hasRows ? [] : visibleNodes(tree, collapsed),
        size: "sm",
        summary: { data: null, position: "bottom" },
      }}
      title="Attribution"
    />
  );
});
